<!-- MaintainLeagues.vue -->
<template>
  <!-- Header / actions -->
  <v-toolbar flat density="comfortable" class="mt-4">
    <v-toolbar-title>ADMIN League Management</v-toolbar-title>
    <v-spacer />
    <v-btn color="primary" prepend-icon="mdi-plus" @click="openDialogForCreate">
      Add New League
    </v-btn>
  </v-toolbar>

  <!-- Error -->
  <v-container v-if="error" class="pt-0">
    <v-alert type="error" variant="tonal" border="start">{{ error }}</v-alert>
  </v-container>

  <!-- Groups -->
  <v-container fluid class="pt-2">
    <v-card rounded="lg" elevation="2">
      <v-expansion-panels variant="accordion">
        <v-expansion-panel
          v-for="grp in grouped"
          :key="grp.LeagueName"
        >
          <v-expansion-panel-title class="text-subtitle-1 font-medium">
            {{ grp.LeagueName }}
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-data-table
              :headers="headers"
              :items="grp.Leagues"
              density="compact"
              item-key="id"
              hide-default-footer
              class="elevation-0"
              :items-per-page="-1"
            >
              <template v-slot:[`item.LeagueTypeSeason`]="{ item }">
                <div class="text-left">{{ item.LeagueTypeSeason }}</div>
              </template>
              <template v-slot:[`item.LeagueSeason`]="{ item }">
                <div class="text-left">{{ item.LeagueSeason }}</div>
              </template>
              <template v-slot:[`item.MatchType`]="{ item }">
                <div class="text-left">{{ item.MatchType }}</div>
              </template>
              <template v-slot:[`item.RuleFramework`]="{ item }">
                <div class="text-left">{{ item.RuleFramework || '—' }}</div>
              </template>
              <template v-slot:[`item.LeagueStartDate`]="{ item }">
                <div class="text-left">{{ item.LeagueStartDate || '—' }}</div>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex ga-1">
                  <v-btn
                    icon="mdi-pencil"
                    size="small"
                    variant="text"
                    @click="openDialogForEdit(item)"
                  />
                  <v-btn
                    icon="mdi-delete"
                    size="small"
                    variant="text"
                    color="error"
                    @click="deleteLeague(item.id)"
                  />
                </div>
              </template>
            </v-data-table>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>

      <div v-if="!grouped.length" class="pa-6 text-medium-emphasis text-center">
        No leagues found for your organization.
      </div>
    </v-card>
  </v-container>

  <!-- Add/Edit dialog -->
  <v-dialog v-model="dialog.open" max-width="640" scrollable>
    <v-card>
      <v-card-title class="text-h6">
        {{ dialog.isEdit ? 'Edit League' : 'Add New League' }}
      </v-card-title>
      <v-card-text>
        <v-form ref="formRef" v-model="valid" @submit.prevent="saveLeague">
          <v-alert type="info" variant="tonal" class="mb-4">
            <div class="text-subtitle-2">League Summary (stored as <code>LeagueTypeSeason</code>)</div>
            <div class="font-mono text-body-2">{{ leagueSummary }}</div>
          </v-alert>

          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Acronym (League)"
                v-model.trim="form.League"
                :rules="[req]"
                density="comfortable"
                clearable
                hide-details="auto"
                autofocus
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Name of League"
                v-model.trim="form.LeagueName"
                :rules="[req]"
                density="comfortable"
                clearable
                hide-details="auto"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-select
                label="Match Type"
                v-model="form.MatchType"
                :items="matchTypes"
                :rules="[req]"
                density="comfortable"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Season of Play"
                v-model.trim="form.LeagueSeason"
                :rules="[req]"
                density="comfortable"
                clearable
                hide-details="auto"
                placeholder="e.g., Fall 2025"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                label="Rule Framework"
                v-model="form.RuleFramework"
                :items="ruleFrameworks"
                :loading="loadingFrameworks"
                :rules="[req]"
                density="comfortable"
                clearable
                hide-details="auto"
                placeholder="Select rule framework"
              >
                <template v-slot:prepend-inner>
                  <v-icon size="small">mdi-book-open-variant</v-icon>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Start Date"
                v-model="form.LeagueStartDate"
                type="date"
                density="comfortable"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Organization"
                v-model="form.Organization"
                readonly
                density="comfortable"
                hide-details="auto"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="closeDialog">Cancel</v-btn>
        <v-btn color="primary" :loading="saving" @click="saveLeague">
          {{ dialog.isEdit ? 'Update' : 'Save' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { leaguesColRef, listMisconductColRef } from "@/firebase"
import { addDoc, getDocs, query, where, doc, updateDoc, deleteDoc } from "firebase/firestore"
import { db } from "@/firebase"
import { ref, computed, onMounted } from "vue"

// ---------- state ----------
const error = ref("")
const saving = ref(false)
const valid = ref(false)
const formRef = ref(null)

const ruleFrameworks = ref([])
const loadingFrameworks = ref(false)

const matchTypes = ["League", "Tournament", "Other"]

const dialog = ref({ open: false, isEdit: false })
const form = ref(resetModel())

const flat = ref([]) // flat list of leagues
const grouped = computed(() => groupByLeagueName(flat.value))

const headers = [
  { title: "League / Type / Season", key: "LeagueTypeSeason", width: 320 },
  { title: "Season", key: "LeagueSeason", width: 140 },
  { title: "Match Type", key: "MatchType", width: 140 },
    { title: "Rule Framework", key: "RuleFramework", width: 160 },
  { title: "Start Date", key: "LeagueStartDate", width: 140 },
  { title: "Actions", key: "actions", sortable: false, width: 120 },
]

// ---------- rules ----------
const req = (v) => (v !== null && v !== undefined && String(v).trim() !== "") || "Required"

// ---------- computed ----------
const leagueSummary = computed(() => {
  const f = form.value
  const league = (f.League || "").trim()
  const season = (f.LeagueSeason || "").trim()
  const mtype = (f.MatchType || "").trim()
  if (!league && !season && !mtype) return ""
  return `${league} ${mtype} -- ${season}`.replace(/\s+/g, " ").trim()
})

// ---------- lifecycle ----------
onMounted(async () => {
  await loadRuleFrameworks()
  await loadLeagues()
})

// ---------- functions ----------

async function loadLeagues() {
  error.value = ""
  try {
    const org = getAdminOrgName()
    if (!org) {
      error.value = "No organization found for current user."
      flat.value = []
      return
    }
    const qLeagues = query(leaguesColRef, where("Organization", "==", org))
    const snap = await getDocs(qLeagues)
    const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }))
    // sort by LeagueName then LeagueTypeSeason
    rows.sort((a, b) =>
      (a.LeagueName || "").localeCompare(b.LeagueName || "") ||
      (a.LeagueTypeSeason || "").localeCompare(b.LeagueTypeSeason || "")
    )
    flat.value = rows
  } catch (e) {
    console.error("loadLeagues", e)
    error.value = "Error loading leagues."
  }
}

async function loadRuleFrameworks() {
  loadingFrameworks.value = true
  try {
    const snap = await getDocs(listMisconductColRef)
    const groups = new Set()
    
    snap.docs.forEach(doc => {
      const data = doc.data()
      if (data.Group && typeof data.Group === 'string') {
        groups.add(data.Group.trim())
      }
    })
    
    // Convert Set to sorted array
    ruleFrameworks.value = Array.from(groups).sort()
    
    console.log('✅ Loaded rule frameworks:', ruleFrameworks.value)
  } catch (e) {
    console.error("loadRuleFrameworks error:", e)
    error.value = "Error loading rule frameworks."
  } finally {
    loadingFrameworks.value = false
  }
}

function groupByLeagueName(list) {
  const map = new Map()
  for (const x of list) {
    const key = x.LeagueName || "(Unnamed)"
    if (!map.has(key)) map.set(key, [])
    map.get(key).push(x)
  }
  return Array.from(map.entries())
    .map(([LeagueName, Leagues]) => ({ LeagueName, Leagues }))
    .sort((a, b) => a.LeagueName.localeCompare(b.LeagueName))
}

function resetModel() {
  return {
    id: "",
    Organization: getAdminOrgName(),
    League: "",
    LeagueName: "",
    LeagueSeason: "",
    MatchType: "League",
    RuleFramework: "",
    LeagueStartDate: "",
  }
}

function getAdminOrgName() {
  return localStorage.getItem("currentUserOrgName") || ""
}

function openDialogForCreate() {
  dialog.value = { open: true, isEdit: false }
  form.value = resetModel()
}

function openDialogForEdit(league) {
  dialog.value = { open: true, isEdit: true }
  form.value = {
    id: league.id,
    Organization: league.Organization || getAdminOrgName(),
    League: league.League || "",
    LeagueName: league.LeagueName || "",
    LeagueSeason: league.LeagueSeason || "",
    MatchType: league.MatchType || "League",
    RuleFramework: league.RuleFramework || "",
    LeagueStartDate: league.LeagueStartDate || "",
  }
}

function closeDialog() {
  dialog.value.open = false
}

async function saveLeague() {
  const res = await formRef.value?.validate()
  if (!res?.valid) return

  saving.value = true
  error.value = ""
  try {
    const payload = {
      Organization: form.value.Organization,
      League: form.value.League,
      LeagueName: form.value.LeagueName,
      LeagueSeason: form.value.LeagueSeason,
      MatchType: form.value.MatchType,
      RuleFramework: form.value.RuleFramework || "",
      LeagueStartDate: form.value.LeagueStartDate || "",
      LeagueTypeSeason: leagueSummary.value,
    }

    if (dialog.value.isEdit && form.value.id) {
      await updateDoc(doc(db, "leagues", form.value.id), payload)
    } else {
      const added = await addDoc(leaguesColRef, payload)
      // store its id for convenience
      await updateDoc(doc(db, "leagues", added.id), { LeagueID: added.id })
    }

    await loadLeagues()
    closeDialog()
  } catch (e) {
    console.error("saveLeague", e)
    error.value = "Error saving league."
  } finally {
    saving.value = false
  }
}

async function deleteLeague(id) {
  if (!confirm("Delete this league?")) return
  try {
    await deleteDoc(doc(db, "leagues", id))
    await loadLeagues()
  } catch (e) {
    console.error("deleteLeague", e)
    error.value = "Error deleting league."
  }
}
</script>

<style scoped>
/* keep it minimal — Vuetify provides most styling */
</style>
